import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CreditCard, Loader2, Settings, Users } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { billingService } from "@/services/billingService";
import type {
  CreditGateRequiredAction,
  CreditGateSurface,
} from "@/hooks/useCreditGateModal";

import { BillingAddonsCard } from "./BillingAddonsCard";
import { BillingPlanOptionCard } from "./BillingPlanOptionCard";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  balance?: string | number | null;
  userEffectiveAvailable?: string | number | null;
  requiredAction?: CreditGateRequiredAction;
  context?: CreditGateSurface;
};

/** Mirrors LIVE_SUBSCRIPTION_STATUSES on the checkout endpoint. */
const LIVE_SUBSCRIPTION_STATUSES = new Set(["active", "in_trial", "trialing"]);

const bodyCopy = (
  context?: CreditGateSurface,
  requiredAction?: CreditGateRequiredAction,
  hasLiveSubscription = false,
): string => {
  if (requiredAction === "ask_company_admin_to_allocate") {
    return "This action needs credits on a user account. Allocate credits from your company pool or subscribe to add more.";
  }
  const surface =
    context === "agent" || context === "artifact"
      ? "complete this request"
      : "process this meeting";
  if (hasLiveSubscription) {
    return `Your company does not have enough credits to ${surface}. Add a credit pack, or allocate credits to restore access.`;
  }
  return `Your company does not have enough credits to ${surface}. Subscribe, add a credit pack, or allocate credits to restore access.`;
};

export const InsufficientCreditsUpgradeDialog = ({
  open,
  onOpenChange,
  balance,
  userEffectiveAvailable,
  requiredAction,
  context,
}: Props) => {
  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);
  const showSubscribePath = requiredAction !== "ask_company_admin_to_allocate";

  const summaryQuery = useQuery({
    queryKey: ["billing", "summary"],
    queryFn: billingService.getSummary,
    enabled: open,
  });

  const summary = summaryQuery.data;
  // Checkout opens a new Airwallex subscription on a fresh cycle.
  const hasLiveSubscription = Boolean(
    summary?.billing_plan_id &&
      LIVE_SUBSCRIPTION_STATUSES.has(summary?.subscription_status ?? ""),
  );
  const offerCheckout = showSubscribePath && !hasLiveSubscription;

  const pricesQuery = useQuery({
    queryKey: ["billing", "prices"],
    queryFn: billingService.getPrices,
    enabled: open && offerCheckout && summaryQuery.isSuccess,
  });

  const prices = pricesQuery.data?.items ?? [];
  const priceId = selectedPriceId || summary?.checkout_price_id || null;
  const canStartCheckout = Boolean(
    offerCheckout && summary?.checkout_enabled && priceId,
  );

  const checkoutMutation = useMutation({
    mutationFn: async () => {
      if (!priceId) throw new Error("Select a plan to continue.");
      return billingService.createCheckout({ price_id: priceId });
    },
    onSuccess: (data) => {
      if (data.checkout_url) {
        window.location.assign(data.checkout_url);
      }
    },
    onError: (error: Error) => {
      toast.error("Checkout failed", { description: error.message });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Insufficient credits</DialogTitle>
          <DialogDescription>
            {bodyCopy(context, requiredAction, hasLiveSubscription)}
          </DialogDescription>
        </DialogHeader>

        {userEffectiveAvailable !== undefined && userEffectiveAvailable !== null ? (
          <p className="text-sm text-muted-foreground">
            Effective available: {Number(userEffectiveAvailable).toLocaleString()} credits
          </p>
        ) : balance !== undefined && balance !== null ? (
          <p className="text-sm text-muted-foreground">
            Current balance: {Number(balance).toLocaleString()} credits
          </p>
        ) : null}

        {requiredAction === "ask_company_admin_to_allocate" ? (
          <div className="rounded-lg border p-4 text-sm">
            <p className="mb-3 text-muted-foreground">
              Credits remain in the company pool but are not assigned to this user yet.
            </p>
            <Button asChild>
              <Link to="/company/users">
                <Users className="mr-2 h-4 w-4" />
                Allocate credits on Company Users
              </Link>
            </Button>
          </div>
        ) : null}

        {showSubscribePath ? (
          <>
            {offerCheckout ? (
              pricesQuery.isLoading ? (
                <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading plans…
                </div>
              ) : prices.length > 0 ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  {prices.map((price) => (
                    <BillingPlanOptionCard
                      key={price.id}
                      plan={price}
                      selected={priceId === price.airwallex_price_id}
                      onSelect={setSelectedPriceId}
                    />
                  ))}
                </div>
              ) : (
                <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
                  No plans are available yet. Contact your administrator.
                </div>
              )
            ) : null}

            {summary ? <BillingAddonsCard summary={summary} /> : null}

            {hasLiveSubscription ? (
              <div className="rounded-lg border p-4 text-sm">
                <p className="mb-3 text-muted-foreground">
                  You are already on{" "}
                  <span className="font-medium text-foreground">
                    {summary?.billing_plan_name ?? "a paid plan"}
                  </span>
                  . Moving to a larger plan is charged pro rata for the days left
                  in this period, so it is handled on the billing page.
                </p>
                <Button asChild variant="outline">
                  <Link to="/company/billing">
                    <Settings className="mr-2 h-4 w-4" />
                    Change plan on Billing
                  </Link>
                </Button>
              </div>
            ) : null}

            {offerCheckout && summaryQuery.data && !summary?.checkout_enabled ? (
              <p className="text-xs text-muted-foreground">
                Checkout is not connected in this environment yet.
              </p>
            ) : null}
          </>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Not now
          </Button>
          {offerCheckout ? (
            <Button
              onClick={() => checkoutMutation.mutate()}
              disabled={checkoutMutation.isPending || !canStartCheckout}
            >
              {checkoutMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CreditCard className="mr-2 h-4 w-4" />
              )}
              Continue to checkout
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InsufficientCreditsUpgradeDialog;
